import {
  BuildingOfficeIcon,
  HomeIcon,
  MapPinIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

import {
  buildFullName,
  EntityCard,
  EntityCardHeader,
  EntityCardSection,
  EntityDisclosure,
  formatEnum,
} from '@/components/shared';
import type {
  ReadProperty,
  ReadPropertyWithOwners,
} from '@/services/api/subjectsService';

interface PropertyCardProps {
  property: ReadProperty | ReadPropertyWithOwners;
  showOwners?: boolean;
  onEdit?: () => void;
}

export const PropertyCard = ({
  property,
  showOwners = false,
  onEdit,
}: PropertyCardProps) => {
  const displayName =
    property.property_name ||
    [property.physical_address?.line1, property.physical_address?.city]
      .filter(Boolean)
      .join(', ') ||
    'Property';

  const hasOwners =
    showOwners &&
    'owners' in property &&
    property.owners &&
    property.owners.length > 0;

  const hasAddress =
    property.physical_address?.line1 ||
    property.physical_address?.city ||
    property.physical_address?.state;

  const hasAdditionalDetails =
    hasAddress || property.parcel_id || property.legal_description;

  return (
    <EntityCard icon={<HomeIcon />} onEdit={onEdit}>
      <EntityCardHeader title={displayName} />

      {property.involvement_description && (
        <p className="mt-1 text-sm text-gray-600">
          {property.involvement_description}
        </p>
      )}

      <div className="mt-1 text-sm text-gray-500 capitalize">
        {formatEnum(property.real_property_type)}
      </div>

      {property.physical_address?.city && property.physical_address?.state && (
        <div className="mt-1 text-sm text-gray-600">
          {property.physical_address.city}, {property.physical_address.state}
          {property.physical_address.zip && ` ${property.physical_address.zip}`}
        </div>
      )}

      {/* Owners */}
      {hasOwners && (
        <EntityCardSection className="mt-2 space-y-1.5">
          {(property as ReadPropertyWithOwners).owners?.map((ownership) => {
            const { party, ownership_type } = ownership;
            let partyName: string;

            if (party.party_type === 'person') {
              const firstName = 'first_name' in party ? party.first_name : null;
              const lastName = 'last_name' in party ? party.last_name : null;
              partyName =
                buildFullName(firstName, undefined, lastName) ||
                party.short_description ||
                'Unnamed';
            } else {
              const legalName =
                'legal_name' in party
                  ? (party.legal_name as string | null)
                  : null;
              const dbaName =
                'dba_name' in party ? (party.dba_name as string | null) : null;
              partyName =
                legalName || dbaName || party.short_description || 'Unnamed';
            }

            return (
              <div
                key={ownership.id}
                className="flex items-center gap-1.5 text-sm"
              >
                {party.party_type === 'person' ? (
                  <UserIcon className="h-4 w-4 text-gray-400" />
                ) : (
                  <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                )}
                <div>
                  <span className="text-xs text-gray-500 capitalize">
                    {ownership_type !== 'owner'
                      ? formatEnum(ownership_type)
                      : 'Owner'}{' '}
                    ·{' '}
                  </span>
                  <span className="text-gray-900 font-medium">{partyName}</span>
                </div>
              </div>
            );
          })}
        </EntityCardSection>
      )}

      {/* Details disclosure */}
      {hasAdditionalDetails && (
        <EntityDisclosure>
          {hasAddress && (
            <div className="space-y-1">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Address
              </div>
              <div className="flex items-start gap-2 text-sm">
                <MapPinIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="text-gray-900 space-y-0.5">
                  {property.physical_address?.line1 && (
                    <div>{property.physical_address.line1}</div>
                  )}
                  {property.physical_address?.line2 && (
                    <div>{property.physical_address.line2}</div>
                  )}
                  {(property.physical_address?.city ||
                    property.physical_address?.state) && (
                    <div>
                      {[
                        property.physical_address.city,
                        property.physical_address.state,
                        property.physical_address.zip,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {property.parcel_id && (
            <div className="space-y-1">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Parcel ID
              </div>
              <div className="text-sm text-gray-900">{property.parcel_id}</div>
            </div>
          )}

          {property.legal_description && (
            <div className="space-y-1">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Legal Description
              </div>
              <div className="text-xs text-gray-700 leading-relaxed">
                {property.legal_description}
              </div>
            </div>
          )}
        </EntityDisclosure>
      )}
    </EntityCard>
  );
};
